"use client";
import {
  Button,
  IconButton,
  tw
} from "./chunk-DGC4337H.js";

// src/badge/Badge.tsx
import { jsx } from "react/jsx-runtime";
function Badge({ color = "gray", className, children }) {
  return /* @__PURE__ */ jsx(
    "span",
    {
      className: `inline-flex items-center rounded-full px-2.5 py-0.5 font-medium text-xs ${colorStyles[color]} ${className != null ? className : ""}`,
      children
    }
  );
}
var colorStyles = {
  gray: tw`bg-disabled text-fg`,
  primary: tw`bg-primary/15 text-primary`,
  green: tw`bg-success/15 text-success`,
  amber: tw`bg-warn/15 text-warn`,
  info: tw`bg-info/15 text-info`,
  error: tw`bg-error/15 text-error`
};

// src/data-grid/index.tsx
import dynamic from "next/dynamic";
var DataGrid = dynamic(
  () => import("./DataGrid-EMFWYR5Q.js").then((m) => m.DataGrid),
  { ssr: false }
);

// src/dialog/index.ts
import { Dialog as BaseDialog } from "@base-ui/react/dialog";

// src/dialog/DialogActions.tsx
import { jsx as jsx2 } from "react/jsx-runtime";
function DialogActions({ children, className }) {
  return /* @__PURE__ */ jsx2(
    "div",
    {
      className: `flex flex-row items-center justify-end gap-2 border-t border-disabled px-6 py-4 ${className != null ? className : ""}`,
      children
    }
  );
}

// src/dialog/DialogContent.tsx
import { Dialog } from "@base-ui/react/dialog";
import { XIcon } from "lucide-react";
import { jsx as jsx3, jsxs } from "react/jsx-runtime";
function DialogContent({ title, description, children }) {
  return /* @__PURE__ */ jsxs(Dialog.Portal, { children: [
    /* @__PURE__ */ jsx3(Dialog.Backdrop, { className: "fixed inset-0 z-50 bg-black/30 transition-opacity" }),
    /* @__PURE__ */ jsx3("div", { className: "fixed inset-0 z-50 flex overflow-auto p-4", children: /* @__PURE__ */ jsxs(Dialog.Popup, { className: "m-auto flex w-full min-w-0 max-w-md flex-col rounded-xl bg-surface shadow-lg", children: [
      /* @__PURE__ */ jsxs("div", { className: "px-6 pt-6 pb-5", children: [
        /* @__PURE__ */ jsxs(Dialog.Title, { className: "flex flex-row items-center", children: [
          /* @__PURE__ */ jsx3("span", { className: "grow font-medium text-[15px] text-fg", children: title }),
          /* @__PURE__ */ jsx3(Dialog.Close, { render: /* @__PURE__ */ jsx3(IconButton, {}), children: /* @__PURE__ */ jsx3(XIcon, {}) })
        ] }),
        description && /* @__PURE__ */ jsx3(Dialog.Description, { className: "mt-1.5 text-[13px] text-muted leading-relaxed", children: description })
      ] }),
      /* @__PURE__ */ jsx3("div", { className: "px-6 pb-1", children })
    ] }) })
  ] });
}

// src/dialog/index.ts
var Dialog2 = {
  Root: BaseDialog.Root,
  Trigger: BaseDialog.Trigger,
  Close: BaseDialog.Close,
  Content: DialogContent,
  Actions: DialogActions
};

// src/dialog/ConfirmationDialog.tsx
import { AlertDialog } from "@base-ui/react/alert-dialog";
import { useTranslations } from "next-intl";
import { useSyncExternalStore } from "react";
import { jsx as jsx4, jsxs as jsxs2 } from "react/jsx-runtime";
function ConfirmationDialog() {
  var _a, _b;
  const t = useTranslations("ConfirmationDialog");
  const prompt = useSyncExternalStore(subscribe, getPrompt, getPrompt);
  if (!prompt) {
    return null;
  }
  return /* @__PURE__ */ jsx4(
    AlertDialog.Root,
    {
      open: true,
      onOpenChange: () => {
        close();
      },
      children: /* @__PURE__ */ jsxs2(AlertDialog.Portal, { children: [
        /* @__PURE__ */ jsx4(AlertDialog.Backdrop, { className: "fixed inset-0 z-50 bg-black/30 transition-opacity" }),
        /* @__PURE__ */ jsx4("div", { className: "fixed inset-0 z-50 flex items-center justify-center", children: /* @__PURE__ */ jsxs2(AlertDialog.Popup, { className: "w-full max-w-150 rounded-xl border border-disabled bg-surface p-6 shadow-lg", children: [
          /* @__PURE__ */ jsx4(AlertDialog.Title, { className: "font-medium text-lg", children: prompt.title }),
          prompt.description && /* @__PURE__ */ jsx4(AlertDialog.Description, { className: "mt-2 text-base text-muted", children: prompt.description }),
          /* @__PURE__ */ jsxs2("div", { className: "mt-4 flex flex-row justify-end gap-2", children: [
            /* @__PURE__ */ jsx4(
              AlertDialog.Close,
              {
                render: /* @__PURE__ */ jsx4(
                  Button,
                  {
                    variant: "outline",
                    size: "md",
                    onClick: () => {
                      var _a2;
                      (_a2 = prompt.onDecline) == null ? void 0 : _a2.call(prompt);
                      close();
                    }
                  }
                ),
                children: (_a = prompt.declineLabel) != null ? _a : t("declineButton")
              }
            ),
            /* @__PURE__ */ jsx4(
              AlertDialog.Close,
              {
                render: /* @__PURE__ */ jsx4(
                  Button,
                  {
                    variant: "solid",
                    color: prompt.danger === false ? "primary" : "error",
                    size: "md",
                    onClick: () => {
                      prompt.onAccept();
                      close();
                    }
                  }
                ),
                children: (_b = prompt.acceptLabel) != null ? _b : t("acceptButton")
              }
            )
          ] })
        ] }) })
      ] })
    }
  );
}
var confirmationPrompt;
var listeners = [];
function confirmAction(prompt) {
  confirmationPrompt = prompt;
  notify();
}
function subscribe(listener) {
  listeners = [...listeners, listener];
  return () => {
    listeners = listeners.filter((l) => l !== listener);
  };
}
function getPrompt() {
  return confirmationPrompt;
}
function notify() {
  for (const listener of listeners) {
    listener();
  }
}
function close() {
  confirmationPrompt = void 0;
  notify();
}
export {
  Badge,
  Button,
  ConfirmationDialog,
  DataGrid,
  Dialog2 as Dialog,
  confirmAction
};
